import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AlertCircle } from 'lucide-react'

type Step = 1 | 2

const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦' },
  { code: 'sw', label: 'Swahili',    flag: '🇰🇪' },
]

const INSTITUTION_TYPES = [
  {
    value: 'standard',
    label: 'Standard Institution',
    desc:  'Government body, ministry, department, or corporate communications team.',
  },
  {
    value: 'association',
    label: 'Association',
    desc:  'Membership body, journalist professional association, trade union, or industry group.',
  },
  {
    value: 'journalist',
    label: 'Journalist Institution',
    desc:  'Press agency, media house, newsroom, or broadcast organisation.',
  },
  {
    value: 'individual',
    label: 'Individual',
    desc:  'Individual journalist, freelancer, or independent media professional.',
  },
]

const PERMISSIONS: Record<string, Record<string, string>> = {
  'CCO':       { dashboard:'full', media_registry:'full', accreditation:'full', events:'full', coverage_analytics:'full', awards:'full', social_media:'full', organogram:'full', media_kits:'full', journalist_portal:'full' },
  'Admin':     { dashboard:'view', media_registry:'edit', accreditation:'edit', events:'edit', coverage_analytics:'view', awards:'edit', social_media:'edit', organogram:'none', media_kits:'edit', journalist_portal:'view' },
  'President': { dashboard:'full', media_registry:'full', accreditation:'full', events:'full', coverage_analytics:'full', awards:'full', social_media:'full', organogram:'full', media_kits:'full', journalist_portal:'full' },
  'Secretary': { dashboard:'view', media_registry:'edit', accreditation:'edit', events:'edit', coverage_analytics:'view', awards:'edit', social_media:'edit', organogram:'none', media_kits:'edit', journalist_portal:'view' },
  'Owner':     { dashboard:'full', media_registry:'full', accreditation:'full', events:'full', coverage_analytics:'full', awards:'full', social_media:'full', organogram:'full', media_kits:'full', journalist_portal:'full' },
}

const MODULES = [
  'dashboard','media_registry','accreditation','events',
  'coverage_analytics','awards','social_media','organogram','media_kits','journalist_portal',
]

// System roles seeded per org type
const SYSTEM_ROLES_BY_TYPE: Record<string, Array<{ name: string; description: string }>> = {
  standard:    [
    { name: 'CCO',   description: 'Institution Admin: full access, primary administrator' },
    { name: 'Admin', description: 'General administrator: broad access to aid the Institution Admin' },
  ],
  journalist:  [
    { name: 'CCO',   description: 'Institution Admin / Lead Journalist: full access' },
    { name: 'Admin', description: 'General administrator: broad access to aid the Institution Admin' },
  ],
  association: [
    { name: 'President', description: 'President: full access, primary leader of the association' },
    { name: 'Secretary', description: 'Secretary: administrative support; can manage members and edit association details' },
  ],
  individual:  [
    { name: 'Owner', description: 'Account owner: full access' },
  ],
}

const DEFAULT_FORM = {
  name:             '',
  slug:             '',
  locale_default:   'en',
  org_type:         'standard',
  extra_languages:  [] as string[],
}

interface CreateOrgWizardProps {
  open:         boolean
  onClose:      () => void
  onSuccess?:   (orgId: string, orgName: string) => void
  initialName?: string
}

export function CreateOrgWizard({ open, onClose, onSuccess, initialName }: CreateOrgWizardProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>(1)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [createdOrg, setCreatedOrg] = useState<{ id: string; name: string } | null>(null)

  const slugify = (s: string) =>
    s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

  useEffect(() => {
    if (open && initialName) {
      setForm(f => ({ ...f, name: initialName, slug: slugify(initialName) }))
    }
  }, [open, initialName])

  function toggleExtraLang(code: string) {
    setForm(f => ({
      ...f,
      extra_languages: f.extra_languages.includes(code)
        ? f.extra_languages.filter(c => c !== code)
        : [...f.extra_languages, code],
    }))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // 1. Insert org
      const { data: org, error: orgError } = await supabase
        .from('organizations' as any)
        .insert({
          name:           form.name,
          slug:           form.slug,
          locale_default: form.locale_default,
          org_type:       form.org_type,
          settings:       { is_platform_owner: false },
        })
        .select()
        .single() as any

      if (orgError) throw orgError

      // 2. Seed type-specific system roles
      const rolesToSeed = SYSTEM_ROLES_BY_TYPE[form.org_type] ?? SYSTEM_ROLES_BY_TYPE.standard
      const { data: insertedRoles, error: rolesError } = await supabase
        .from('roles')
        .insert(rolesToSeed.map(r => ({
          org_id:      (org as any).id,
          name:        r.name,
          description: r.description,
          is_system:   true,
        })))
        .select() as any

      if (rolesError) throw rolesError

      // 3. Seed permissions
      const permRows = ((insertedRoles || []) as any[]).flatMap((role: any) =>
        MODULES.map(mod => ({
          role_id:      role.id,
          module:       mod,
          access_level: PERMISSIONS[role.name]?.[mod] ?? 'none',
        }))
      )
      await supabase.from('role_permissions').insert(permRows as any)

      // 4. Seed org_languages
      const allLangCodes = [
        form.locale_default,
        ...form.extra_languages.filter(c => c !== form.locale_default),
      ]
      await supabase.from('org_languages').insert(
        allLangCodes.map(code => ({
          org_id:        (org as any).id,
          language_code: code,
          is_active:     true,
        }))
      )

      // 5. org_onboarding
      await supabase.from('org_onboarding').upsert(
        { org_id: (org as any).id }, { onConflict: 'org_id' }
      )

      // 6. For association orgs, also create a matching associations record
      //    (DB trigger does this too, but we do it here for immediate visibility)
      if (form.org_type === 'association') {
        await (supabase.from('associations' as any).insert({
          name:             form.name,
          slug:             form.slug,
          status:           'active',
          dues_currency:    'USD',
          dues_period:      'annual',
          grace_period_days: 30,
        }) as any)
        // Intentionally ignore conflict, trigger may have already inserted it
      }

      return org
    },
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['associations'] })
      setCreatedOrg({ id: (org as any).id, name: form.name })
      setStep(2)
    },
  })

  const handleClose = () => {
    setStep(1)
    setForm(DEFAULT_FORM)
    setCreatedOrg(null)
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={o => { if (!o) handleClose() }}>
      <SheetContent side="right" className="w-[540px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b border-[#DDD8CE]">
          <SheetTitle>Create New Institution</SheetTitle>
        </SheetHeader>

        <div className="pt-4 px-1 space-y-5">

          {/* ── STEP 1: Details ── */}
          {step === 1 && (
            <>
              {/* Institution Name */}
              <div className="space-y-1">
                <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">
                  Institution Name *
                </label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
                  placeholder="e.g. ECOWAS Parliament"
                  className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C]"
                />
              </div>

              {/* URL Slug */}
              <div className="space-y-1">
                <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">
                  URL Slug *
                </label>
                <div className="flex items-center border border-[#DDD8CE] rounded-lg overflow-hidden focus-within:border-[#006B3C]">
                  <span className="px-3 py-2 bg-[#F7F5F0] text-[12px] text-muted-foreground border-r border-[#DDD8CE] flex-shrink-0">
                    conorbit.app/
                  </span>
                  <input
                    value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) }))}
                    className="flex-1 px-3 py-2 text-[13px] bg-background focus:outline-none font-mono"
                  />
                </div>
              </div>

              {/* Institution Type */}
              <div className="space-y-2">
                <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">
                  Institution Type *
                </label>
                <div className="space-y-2">
                  {INSTITUTION_TYPES.map(t => (
                    <label
                      key={t.value}
                      className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                        form.org_type === t.value
                          ? 'border-[#006B3C] bg-[#E8F5EE]'
                          : 'border-[#DDD8CE] hover:border-[#006B3C]/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="org_type"
                        value={t.value}
                        checked={form.org_type === t.value}
                        onChange={() => setForm(f => ({ ...f, org_type: t.value }))}
                        className="mt-0.5 accent-[#006B3C]"
                      />
                      <div>
                        <div className="text-[13px] font-semibold text-[#0D2240]">{t.label}</div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5">{t.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Default Language */}
              <div className="space-y-1">
                <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">
                  Primary Language
                </label>
                <select
                  value={form.locale_default}
                  onChange={e => setForm(f => ({ ...f, locale_default: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C]"
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                  ))}
                </select>
              </div>

              {/* Additional Languages (multi-select checkboxes) */}
              <div className="space-y-2">
                <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">
                  Additional Languages
                </label>
                <p className="text-justify text-[11px] text-muted-foreground">
                  Select all official languages this institution communicates in.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {LANGUAGES.map(l => {
                    const isDefault  = l.code === form.locale_default
                    const isSelected = isDefault || form.extra_languages.includes(l.code)
                    return (
                      <label
                        key={l.code}
                        className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                          isDefault
                            ? 'border-[#006B3C] bg-[#E8F5EE] opacity-70 cursor-default'
                            : isSelected
                            ? 'border-[#006B3C] bg-[#E8F5EE]'
                            : 'border-[#DDD8CE] hover:border-[#006B3C]/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDefault}
                          onChange={() => !isDefault && toggleExtraLang(l.code)}
                          className="accent-[#006B3C]"
                        />
                        <span className="text-[13px]">{l.flag}</span>
                        <span className="text-[12.5px] text-[#0D2240]">{l.label}</span>
                        {isDefault && (
                          <span className="text-[10px] text-muted-foreground ml-auto">primary</span>
                        )}
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="p-3 bg-[#F7F5F0] rounded-lg text-[11.5px] text-muted-foreground text-justify">
                Branding, communications email, and KYC documents can be configured later in
                the institution's profile. After creating the institution you will be able to
                add users and assign the primary administrator.
              </div>

              {mutation.isError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-[12px] text-red-700">
                  <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
                  {(mutation.error as Error).message}
                </div>
              )}

              <div className="pt-1 border-t border-[#DDD8CE]">
                <button
                  type="button"
                  disabled={mutation.isPending || !form.name || !form.slug}
                  onClick={() => mutation.mutate()}
                  className="w-full px-4 py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-40 transition-colors"
                >
                  {mutation.isPending ? 'Creating institution…' : 'Create Institution'}
                </button>
              </div>
            </>
          )}

          {/* ── STEP 2: Post-creation ── */}
          {step === 2 && createdOrg && (() => {
            const isAssoc = form.org_type === 'association'
            const isIndividual = form.org_type === 'individual'
            const primaryRole  = isAssoc ? 'President' : isIndividual ? 'Owner' : 'CCO'
            const primaryDesc  = isAssoc
              ? 'President who will lead this association.'
              : isIndividual
              ? 'Owner account for this individual profile.'
              : 'CCO (Chief Communications Officer) who will manage this institution.'
            return (
              <div className="space-y-4">
                <div className="p-4 bg-[#E1F5EE] border border-[#006B3C]/30 rounded-xl">
                  <div className="text-[13px] font-semibold text-[#0F6E56] mb-1">
                    ✓ {createdOrg.name} has been created.
                  </div>
                  <p className="text-[12px] text-[#0F6E56]/80 text-justify mb-3">
                    The next step is to create the {primaryDesc} They will receive login credentials
                    and be prompted to set their own password on first login.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const org = createdOrg
                        handleClose()
                        onSuccess?.(org.id, org.name)
                      }}
                      className="px-4 py-2 bg-[#006B3C] text-white rounded-lg text-[12.5px] font-medium hover:bg-[#005A32]"
                    >
                      Add {primaryRole} now →
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 border border-[#006B3C]/30 text-[#0F6E56] rounded-lg text-[12.5px] hover:bg-[#006B3C]/5"
                    >
                      Do this later
                    </button>
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      </SheetContent>
    </Sheet>
  )
}
